import { FilterPills, Select, type SelectItem } from '@aecom-ai-engineering/react-components';
import { type SelectRootProps } from '@ark-ui/react/select';
import { X } from 'lucide-react';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AGENT_CATEGORY, type AgentTag } from '@/util/service-config';

import { ALL_FILTER, type AgentCategoryFilter, type AgentTagOption } from './filter-agents';

interface AgentsDirectoryToolbarProps {
  category: AgentCategoryFilter;
  onCategoryChange: (category: AgentCategoryFilter) => void;
  selectedTags: AgentTag[];
  onSelectedTagsChange: (tags: AgentTag[]) => void;
  /** Every tag in use, already distinct and sorted. */
  tags: AgentTagOption[];
}

const CATEGORIES = Object.values(AGENT_CATEGORY);

/**
 * Both controls report `string[]`. Matching the value back against the
 * catalogue is what narrows it to the filter union: an empty array — which
 * `deselectable` would produce if it were ever turned on — and anything
 * unrecognised both collapse to "no filter" rather than to a state the page
 * cannot render. That is also how the "All tags" item works: its value is not a
 * tag, so it falls through the lookup.
 */
export const toCategoryFilter = (value: readonly string[]): AgentCategoryFilter =>
  CATEGORIES.find((known) => known === value[0]) ?? ALL_FILTER;

/**
 * The selected tags, in the menu's order rather than the order they were
 * clicked, so the trigger reads "Bids, Risk" either way round and the label does
 * not reshuffle as the selection grows.
 *
 * "All tags" is a reset, not a value: its own value is not a tag, so picking it
 * clears the selection instead of joining it.
 */
export const toTagSelection = (
  value: readonly string[],
  tags: readonly AgentTagOption[],
): AgentTag[] =>
  value.includes(ALL_FILTER)
    ? []
    : tags.filter(({ key }) => value.includes(key)).map(({ key }) => key);

/**
 * `multiple` is missing from `SelectProps`, but the design system spreads its
 * rest props onto Ark's `Select.Root`, which is what reads it — so the prop
 * works today, and this is typed against the machine that consumes it rather
 * than asserted into place. `@ark-ui/react` is a direct dependency here and
 * resolves to the same 5.37.2 the design system builds against, so a rename or
 * re-scope of `multiple` upstream fails the typecheck rather than only the test
 * below.
 *
 * What that leaves uncovered is the design system's half — if it stopped
 * forwarding rest props, or declared `multiple` with its own meaning, this would
 * fall back to single-select silently. `agents-directory-toolbar.test.tsx`
 * selects twice through the real control so that fails loudly instead. The
 * standing ask is for the design system to declare the prop itself.
 *
 * Everything else comes free once the machine is in multiple mode: the trigger's
 * `valueAsString` is the collection's `stringifyItems`, whose separator is ", ",
 * and Select.css gives [data-ds-select-value] `overflow: hidden` with
 * `text-overflow: ellipsis`, so a selection wider than the 208px trigger
 * truncates rather than wrapping.
 */
const MULTIPLE: Pick<SelectRootProps<SelectItem>, 'multiple'> = { multiple: true };

/**
 * Category pills on the left, the tag dropdown on the right.
 *
 * No `py` of its own — the page owns the spacing between its rows, which is why
 * this is not `project-list-toolbar.tsx` with different props.
 *
 * `deselectable` is left alone on both controls: each carries an explicit "All",
 * and clicking the active pill should do nothing rather than clear the row into
 * a state that looks identical but is not.
 */
export const AgentsDirectoryToolbar: FC<AgentsDirectoryToolbarProps> = ({
  category,
  onCategoryChange,
  selectedTags,
  onSelectedTagsChange,
  tags,
}) => {
  const { t } = useTranslation();

  const tagItems = useMemo(
    () => [
      // Kept now that the row is multi-select: clearing by unticking each
      // selected tag in turn is tedious once there are three, and the row needs
      // one visible way back to the unfiltered grid.
      { label: t('agents-directory.filter-by-all'), value: ALL_FILTER },
      ...tags.map(({ key, label }) => ({ label, value: key })),
    ],
    [t, tags],
  );

  return (
    // gap-2 with the pills pushed left by `mr-auto`, rather than
    // justify-between around a group: the DS sizes the trigger with
    // `width: min(100%, var(--ds-space-52))`, and inside a shrink-wrapped flex
    // group that 100% resolves against the group's own content width, which
    // collapses the trigger to 104px. Keeping the Select a direct child of the
    // row resolves it against the row instead.
    //
    // items-start, so when the pills wrap the Select stays level with their
    // first row instead of centring on the whole block. Pills, trigger and
    // clear button are all 36px, so a single row lines up as centring would.
    <div className="flex items-start gap-2">
      <FilterPills
        className="mr-auto"
        items={[
          { label: t('agents-directory.filter-all'), value: ALL_FILTER },
          ...CATEGORIES.map((value) => ({
            label: t(`agents-directory.categories.${value}`),
            value,
          })),
        ]}
        label={t('agents-directory.filter-label')}
        onValueChange={({ value }) => onCategoryChange(toCategoryFilter(value))}
        value={[category]}
      />

      {/* Before the Select rather than after it, which the layout requires
          rather than prefers: the Select is the row's last child, so a sibling
          added on its left leaves its right edge where it was. Placed after it,
          this button appearing on the first pick shoved the trigger 44px left
          and dragged the open menu with it, mid-selection.

          Rendered only once something is applied — a permanent cross beside an
          empty filter is a control that does nothing. Icon only, so the label is
          the accessible name rather than visible text, and the geometry is the
          trigger's own: 36px square, 8px radius, 1px border-primary.

          A sibling rather than inside the trigger, which takes no children — the
          design system would have to offer a clearable Select for that. */}
      {selectedTags.length > 0 && (
        <button
          aria-label={t('agents-directory.filter-clear')}
          className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-[var(--ds-radius-8)] border border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-primary)] text-[var(--ds-color-text-secondary)] hover:border-[var(--ds-color-border-secondary)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ds-color-border-primary)]"
          onClick={() => onSelectedTagsChange([])}
          type="button"
        >
          <X aria-hidden size={16} strokeWidth={1.75} />
        </button>
      )}

      {/* The design system's 208px, and not the frame's 95. Settled, not
          pending: the frame drew a single-select row, so its width predates the
          decision to take several tags, and a list of them has to be legible.

          Both measured before choosing. 95 clips its own label — the trigger's
          12px padding leaves 37px for a 46px "Filter by" — and 104 is the
          narrowest that fits. And a trigger sized to its content cannot hold
          still under `multiple`: it grew 104 to 129 on the first tag and carried
          the menu 68px left while the pointer was still in the list, because
          menuWidth="trigger" anchors the menu to it. menuWidth="default" is no
          escape either, pinning the menu at 208 with an !important min-width so
          that it misaligns under anything narrower. One stable width is the only
          arrangement where the menu holds still across picks. */}
      <Select
        {...MULTIPLE}
        // The applied state, per review: index.css paints the trigger's border
        // with the brand green off this attribute. A raw token, because the
        // palette has no semantic border-brand — only primary, secondary and
        // negative — which is worth adding upstream. The rule lives there rather
        // than in a class here because a Tailwind arbitrary variant naming a
        // data-ds-* hook lands in layer(utilities), which check-css-layers.mjs
        // rejects; see the comment beside it.
        data-agents-tag-filter={selectedTags.length > 0 ? 'applied' : undefined}
        indicator="single"
        items={tagItems}
        label={t('agents-directory.filter-by')}
        onValueChange={({ value }) => onSelectedTagsChange(toTagSelection(value, tags))}
        placeholder={t('agents-directory.filter-by')}
        // An empty selection shows the placeholder rather than "All tags": the
        // unfiltered grid is the resting state, and naming a filter that is not
        // applied suggests one is.
        value={selectedTags}
      />
    </div>
  );
};
